import React, { BaseSyntheticEvent, useEffect, useState } from 'react';
import { Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { IFormElementOptionProp, initialUserFormValues, IUserFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import useUserFormSchema from 'pages/UsersPage/schemaValidation';
import Grid from '@mui/material/Grid';
import { Box } from '@mui/system';
import { FormInput } from 'components/molecules/FormInput';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Typography } from '@mui/material';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { FormSelect } from 'components/molecules/FormSelect';
import {
  EDictionaryPrefixTypesUniqueId,
  mapDictionaryToOptionProp,
  parseMultipleDictionaries,
} from 'helpers/dictionary';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import FormLabel from 'components/atoms/FormLabel';
import { IRole } from 'models/role';
import { useTranslations } from 'hooks/useTranslations';
import { ITranslationKey, Table } from 'components/molecules/Table';
import { useLang } from 'models/langContext';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { useScroll } from 'hooks/useScroll';
import { StyledForm } from 'theme/styles';
import { TableTopButtons } from 'components/atoms/TableTopButtons';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Loader } from 'components/atoms/Loader';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { CardList } from 'components/molecules/CardList';
import {
  IAssignedIndividualLegalResponse,
  IAssignedOrganizationLegalResponse,
} from 'models/account';
import { EOrganisationCategory } from 'constants/OrganisationCategory';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { assignedLEsTableColumns, mapAssignedLEsToDataSource } from '../helpers';
import { AssignedLETableCard } from '../AssignedLETableCard';
import { StyledValueFormHeader } from './styles';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { FormSearchSelect } from 'components/molecules/FormSearchSelect';

export const saveAndAssignLEButtonId = 'users_saveAndAssignLE-button';

export interface IUserForm {
  initialFormValues?: IUserFormValues | null;
  onCancel?: () => void;
  onSubmit: (arg: IUserFormValues, buttonId?: string) => void;
  roles: IRole[];
  legalEntityPrefix: IDictionary[] | null;
  organisationCategory: IDictionary[] | null;
  weaponLicencingAuthority: IDictionary[] | null;
  translationsKeys?: ITranslationKey | any;
  isEdit?: boolean;
  isDisabled?: boolean;
  isAssignedLELoading?: boolean;
  handleDeleteAssignedLegalFromUser?: (id: string, type: ELegalEntityTypes) => void;
  handleAssignedLegalsToUser?: () => void;
  assignedLegalsToUser?: (
    IAssignedIndividualLegalResponse | IAssignedOrganizationLegalResponse
  )[];
}

const UserForm = ({
  onCancel,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSubmit,
  initialFormValues,
  roles,
  legalEntityPrefix,
  organisationCategory,
  weaponLicencingAuthority,
  translationsKeys,
  isEdit = false,
  isDisabled = false,
  isAssignedLELoading,
  handleDeleteAssignedLegalFromUser,
  handleAssignedLegalsToUser,
  assignedLegalsToUser,
}: IUserForm) => {
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const { schema } = useUserFormSchema(!!hasWeaponLicenceModule());
  const isWeaponLicence = !!hasWeaponLicenceModule();
  const { hasPermissions } = usePermission();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IUserFormValues>({
    defaultValues: initialFormValues || initialUserFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IUserFormValues>,
  });

  const [showLicencingAuthority, setShowLicencingAuthority] = useState(false);
  const changePrefixGroup = (val: boolean, itemName?: string) => {
    if (
      itemName &&
      itemName === `formPrefixGroupIds[${EDictionaryPrefixTypesUniqueId.civilianPrefix}]`
    ) {
      setShowLicencingAuthority(val);

      if (isWeaponLicence && val) {
        setValue('formWeaponLicencingAuthority', '', { shouldValidate: true });
      }
    }
  };

  const { samePageAlert, userAlert, clearAlert } = useAlert();
  const { t } = useTranslations();
  const { isDesktop } = useThemeBreakpoint();
  const { selectedLanguage } = useLang();
  const {
    loadDictionaries,
    organisationMilitaryType,
    organisationCivilianType,
    organisationPoliceType,
    organisationStateType,
    phoneCode,
  } = useLegalEntities();
  const { elRef, scrollToError } = useScroll();
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

  useEffect(() => {
    reset(initialFormValues || initialUserFormValues);
  }, [reset, initialFormValues]);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([
        EDictionaryTypes.OrganisationMilitaryType,
        EDictionaryTypes.OrganisationCivilianType,
        EDictionaryTypes.OrganisationPoliceType,
        EDictionaryTypes.OrganisationStateType,
        EDictionaryTypes.PhoneCode,
      ]);
    })();
  }, [selectedLanguage]);

  const onSubmitForm: SubmitHandler<IUserFormValues> = async (
    data: IUserFormValues,
    event?: BaseSyntheticEvent,
  ) => {
    const nativeEvent = event?.nativeEvent as SubmitEvent;
    const submitter = nativeEvent.submitter as HTMLButtonElement;
    const allLicenceAuthorities = data.formWeaponLicencingAuthority === 'all';
    const formWeaponLicencingAuthority = allLicenceAuthorities
      ? [weaponLicencingAuthority?.map((it) => it.uniqueId)].toString()
      : data.formWeaponLicencingAuthority;

    if (!showLicencingAuthority) {
      setValue('formWeaponLicencingAuthority', 'all');
    }

    setIsSubmitLoading(true);
    if (initialFormValues) {
      await onSubmit(
        { ...initialFormValues, ...data, formWeaponLicencingAuthority, allLicenceAuthorities },
        submitter.id,
      );
    } else {
      await onSubmit(
        { ...data, formWeaponLicencingAuthority, allLicenceAuthorities },
        submitter.id,
      );
    }
    setIsSubmitLoading(false);
  };

  useEffect(
    () => {
      clearAlert(ActionTypes.CLEAR_USER_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (initialFormValues) {
      if (initialFormValues.allLicenceAuthorities) {
        setValue('formWeaponLicencingAuthority', 'all');
      }
      if (
        initialFormValues.formPrefixGroupIds &&
        initialFormValues.formPrefixGroupIds[EDictionaryPrefixTypesUniqueId.civilianPrefix] ===
          true
      ) {
        setShowLicencingAuthority(true);
      } else {
        setShowLicencingAuthority(false);
      }
    }
  }, [initialFormValues, setValue]);

  useEffect(() => {
    if (isWeaponLicence && !showLicencingAuthority) {
      setValue('formWeaponLicencingAuthority', 'all', { shouldValidate: true });
    }
  }, [showLicencingAuthority, isWeaponLicence, setValue]);

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  const allLicencingAuthoritiesOption: IFormElementOptionProp = {
    label: t(translationsKeys.weaponLicencingAuthorityAll),
    value: 'all',
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <StyledForm
        container
        size={{ xs: 12 }}
        columnSpacing={2.5}
        ref={elRef}
        style={{ marginBottom: '20px' }}
      >
        {isDisabled && (
          <Grid size={{ xs: 12 }} style={{ margin: '10px 0 20px' }}>
            <Alert text={t('users.addUser.limit.warning')} variant={EAlertVariants.warning} />
          </Grid>
        )}

        <Grid container size={{ xs: 12 }} sx={{ my: '20px' }}>
          <StyledValueFormHeader>{t('user.userDetails.section')}</StyledValueFormHeader>
        </Grid>

        <Grid size={{ xs: 12, md: 12, lg: 3 }}>
          <Box sx={{ mb: 1 }}>
            <FormInput
              name="email"
              label={`${t(translationsKeys.emailLabel)}*`}
              control={control}
              disabled={isEdit || isDisabled}
              setValue={setValue}
              errors={errors}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <Box sx={{ mb: 1 }}>
            <FormInput
              name="firstName"
              label={`${t(translationsKeys.firstNameLabel)}*`}
              control={control}
              setValue={setValue}
              errors={errors}
              charsLimit={100}
              disabled={isDisabled}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <Box sx={{ mb: 1 }}>
            <FormInput
              name="lastName"
              label={`${t(translationsKeys.lastNameLabel)}*`}
              control={control}
              errors={errors}
              setValue={setValue}
              charsLimit={100}
              disabled={isDisabled}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }} container columnSpacing={2.5}>
          <Grid size={{ xs: 6, md: 6, lg: 3 }}>
            {phoneCode && (
              <FormSearchSelect
                name="phoneNumberCodeDictionaryUniqueId"
                options={mapDictionaryToOptionProp(phoneCode)}
                label={t(translationsKeys.phonePrefixLabel)}
                required
                control={control}
                errors={errors}
                disabled={isDisabled}
              />
            )}
          </Grid>
          <Grid size={{ xs: 6, md: 6, lg: 3 }}>
            <FormInput
              name="phoneNumberValue"
              label={`${t(translationsKeys.phoneNumberLabel)}*`}
              control={control}
              setValue={setValue}
              errors={errors}
              charsLimit={20}
              disabled={isDisabled}
            />
          </Grid>
        </Grid>

        <Grid size={{ xs: 12 }} columnSpacing={2.5}>
          <StyledValueFormHeader style={{ margin: '20px 0' }}>
            {t('user.userRoleManagementSettings.section')}
          </StyledValueFormHeader>
          <Grid size={{ xs: 12 }} container columnSpacing={2.5}>
            <Grid size={{ xs: 6, md: 6, lg: 3 }}>
              <Box sx={{ mb: 1 }}>
                <FormLabel name="formRoleIds" label={`${t(translationsKeys.roleLabel)}*`} />
                {roles.map((role) => (
                  <FormCheckbox
                    key={role.uniqueId}
                    name={`formRoleIds[${role.uniqueId}]`}
                    label={role.name}
                    control={control}
                    errors={errors}
                    disabled={isDisabled}
                  />
                ))}
                <FormErrorLabel label={errors?.formRoleIds?.message?.toString() as string} />
              </Box>
            </Grid>
            <Grid size={{ xs: 6, md: 6, lg: 3 }}>
              <Box sx={{ mb: 1 }}>
                <FormLabel
                  name="formPrefixGroupIds"
                  label={`${t(translationsKeys.legalEntityPrefix)}*`}
                />
                {legalEntityPrefix &&
                  legalEntityPrefix.map((prefix) => (
                    <FormCheckbox
                      key={prefix.uniqueId}
                      name={`formPrefixGroupIds[${prefix.uniqueId}]`}
                      label={prefix.name}
                      control={control}
                      errors={errors}
                      disabled={isDisabled}
                      onChange={(e, item) => changePrefixGroup(e, item)}
                    />
                  ))}
                {organisationCategory &&
                  organisationCategory
                    .filter((category) => category.uniqueId === EOrganisationCategory.state)
                    .map((category) => (
                      <FormCheckbox
                        key={category.uniqueId}
                        name={`formOrganisationCategoryIds[${category.uniqueId}]`}
                        label={category.name}
                        control={control}
                        errors={errors}
                        disabled={isDisabled}
                      />
                    ))}
                <FormErrorLabel
                  label={errors?.formPrefixGroupIds?.message?.toString() as string}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Box sx={{ mb: 1 }}>
                {weaponLicencingAuthority &&
                  showLicencingAuthority &&
                  hasWeaponLicenceModule() && (
                    <FormSelect
                      name="formWeaponLicencingAuthority"
                      options={[
                        allLicencingAuthoritiesOption,
                        ...mapDictionaryToOptionProp(weaponLicencingAuthority),
                      ]}
                      label={`${t(translationsKeys.weaponLicencingAuthority)}*`}
                      setValue={setValue}
                      control={control}
                      errors={errors}
                      disabled={isDisabled}
                      canBeEmpty={false}
                    />
                  )}
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {isEdit && hasPermissions(EPermission.VIEW_LE) && (
          <Grid container size={{ xs: 12 }} sx={{ mt: 3, mb: 2 }}>
            <StyledValueFormHeader style={{ marginBottom: '10px' }}>
              {t('userDetails.assignedLegalEntity.section')}
            </StyledValueFormHeader>

            <Grid size={{ xs: 12 }} sx={{ mb: '6px' }}>
              <TableTopButtons>
                <Button
                  id="users_assignLegalEntity-button"
                  label={t('userDetails.assignLegalEntity.button')}
                  variant={EButtonVariants.contained}
                  onClick={handleAssignedLegalsToUser}
                  fullWidth={!isDesktop}
                  permission={[EPermission.VIEW_LE, EPermission.VIEW_ALL_LE]}
                  permissionOr
                />
              </TableTopButtons>
            </Grid>
            <Grid size={{ xs: 12 }}>
              {isAssignedLELoading ? (
                <Loader isLoading={isAssignedLELoading} />
              ) : (
                <div>
                  {isDesktop ? (
                    <Table
                      columns={assignedLEsTableColumns({
                        handleDelete: (LEId, type) =>
                          handleDeleteAssignedLegalFromUser
                            ? handleDeleteAssignedLegalFromUser(LEId, type)
                            : {},
                      })}
                      dataSource={mapAssignedLEsToDataSource(
                        assignedLegalsToUser ?? [],
                        parseMultipleDictionaries(
                          [
                            organisationStateType,
                            organisationCivilianType,
                            organisationMilitaryType,
                            organisationPoliceType,
                          ],
                          'name',
                        ),
                        t,
                      )}
                    />
                  ) : (
                    <CardList
                      items={mapAssignedLEsToDataSource(
                        assignedLegalsToUser ?? [],
                        parseMultipleDictionaries(
                          [
                            organisationStateType,
                            organisationCivilianType,
                            organisationMilitaryType,
                            organisationPoliceType,
                          ],
                          'name',
                        ),
                        t,
                      )}
                      render={(data, index) => (
                        <Box sx={{ mb: 2 }}>
                          <AssignedLETableCard
                            data={data}
                            key={index}
                            handleDelete={() =>
                              handleDeleteAssignedLegalFromUser
                                ? handleDeleteAssignedLegalFromUser(data.id, data.type)
                                : {}
                            }
                          />
                        </Box>
                      )}
                    />
                  )}
                </div>
              )}
            </Grid>
          </Grid>
        )}
        <Grid container size={{ xs: 12 }} columnSpacing={2.5} sx={{ rowGap: 2.5, mt: 2 }}>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Button
              type="submit"
              fullWidth
              label={t('general.save.button')}
              id="users_save-button"
              variant={EButtonVariants.contained}
              isLoading={isSubmitLoading}
              disabled={isDisabled}
            />
          </Grid>

          {!isEdit && (
            <Grid size={{ xs: 12, lg: 3 }}>
              <Button
                type="submit"
                fullWidth
                label={t('general.saveAndAssignedLegals.button')}
                id={saveAndAssignLEButtonId}
                variant={EButtonVariants.contained}
                isLoading={isSubmitLoading}
                disabled={isDisabled}
              />
            </Grid>
          )}

          {onCancel && (
            <Grid size={{ xs: 12, lg: 3 }}>
              <Button
                fullWidth
                label={t('general.cancel.button')}
                id="users_cancel-button"
                variant={EButtonVariants.outlined}
                onClick={onCancel}
              />
            </Grid>
          )}
        </Grid>

        <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
          <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ mt: 3 }}>
          {samePageAlert && (
            <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
          )}
          {userAlert && <Alert text={userAlert.text} variant={userAlert.variant} />}
        </Grid>
      </StyledForm>
    </form>
  );
};

export { UserForm };
